import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import {
  Play, Pause, SkipForward, RotateCcw, Volume2, VolumeX,
  Zap, Clock, BookOpen, X, ChevronRight, Loader2, CheckCircle2,
  Compass, ArrowRight, ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  { id: 'quick', label: 'Quick Guide', time: '1-2 min', icon: Zap, desc: 'Get the basics fast', color: 'border-emerald-300 bg-emerald-50' },
  { id: 'standard', label: 'Standard Demo', time: '3-5 min', icon: Clock, desc: 'See how it works', color: 'border-blue-300 bg-blue-50' },
  { id: 'deep', label: 'Deep Dive', time: '7-10 min', icon: BookOpen, desc: 'Master the platform', color: 'border-violet-300 bg-violet-50' },
];

const DEMO_SCRIPTS = {
  revq: {
    quick: [
      { title: 'Welcome to revQ', text: "Welcome to rev Q, your AI-powered billing and revenue cycle management system.", highlight: null, route: '/revq' },
      { title: 'Claims Dashboard', text: "Here's your claims dashboard. You can see all open claims, their status, and the full lifecycle from draft to paid.", highlight: '[data-testid="claims-pipeline"]', route: '/revq' },
      { title: 'Copy Claim Data', text: "The one-click Copy Claim Data feature lets you instantly copy any claim data to paste into your clearinghouse or EHR.", highlight: '[data-testid="copy-note-btn-top"]', route: '/revq' },
      { title: "You're All Set!", text: "That's rev Q in a nutshell: AI-powered claims, denial management, and one-click data sharing. Ready to go!", highlight: null, route: '/revq' },
    ],
    standard: [
      { title: 'Welcome to revQ', text: "Welcome to rev Q. Let's walk through how the billing workflow actually works day to day.", highlight: null, route: '/revq' },
      { title: 'Workflow Guide', text: "Start here at the dashboard. The workflow guide shows you the 6 steps from verifying eligibility to reconciling payments.", highlight: '[data-testid="workflow-tracker"]', route: '/revq' },
      { title: 'Claims Pipeline', text: "The claims pipeline at the top tracks every claim visually, from draft through submission to payment.", highlight: '[data-testid="claims-pipeline"]', route: '/revq' },
      { title: 'AI Scrubber', text: "When you create a new claim, the AI scrubber catches coding errors before submission, saving you denial headaches.", highlight: null, route: '/revq/ai-claims' },
      { title: 'AI Fix & Resubmit', text: "If a claim does get denied, the AI Fix and Resubmit feature automatically identifies the issue and suggests a correction.", highlight: null, route: '/revq/denials' },
      { title: 'Revenue Forecast', text: "Rev Q also forecasts your collections 30, 60, and 90 days out using AI analysis of your historical data.", highlight: null, route: '/revq/analytics' },
      { title: 'Workflow Complete', text: "That covers the core rev Q workflow. Every step is designed to reduce clicks and catch errors before they cost you money.", highlight: null, route: '/revq' },
    ],
    deep: [
      { title: 'Welcome', text: "Welcome to the deep dive of rev Q. We'll cover the complete revenue cycle from start to finish.", highlight: null, route: '/revq' },
      { title: 'Claims Dashboard', text: "Let's start with the claims dashboard. Notice the pipeline tracker at the top, it gives you an instant snapshot of where every claim stands.", highlight: '[data-testid="claims-pipeline"]', route: '/revq' },
      { title: 'Workflow Guide', text: "The workflow guide on the left walks you through 6 daily steps. Each step has built-in Lens document intelligence, so you can scan insurance cards and superbills right from here.", highlight: '[data-testid="workflow-tracker"]', route: '/revq' },
      { title: 'Claim Wizard - Step 1', text: "Creating a new claim uses a 4-step wizard. Step 1 captures patient and payer info. Step 2 handles diagnosis and procedure codes with AI auto-suggest.", highlight: null, route: '/revq/ai-claims' },
      { title: 'AI Scrubber', text: "Step 3 runs the AI scrubber, which checks for coding conflicts, missing modifiers, and payer-specific rules before you submit.", highlight: null, route: '/revq/ai-claims' },
      { title: 'CMS Preview', text: "Step 4 gives you a CMS fifteen hundred preview so you can verify everything before sending to the clearinghouse.", highlight: null, route: '/revq/ai-claims' },
      { title: 'Denial Patterns', text: "On the denials side, the AI identifies denial patterns across your practice. The heatmap shows which payer and CPT code combinations are causing the most rejections.", highlight: null, route: '/revq/denials' },
      { title: 'AI Fix', text: "For individual denials, the AI Fix and Resubmit button analyzes the denial reason, suggests a corrected claim, and lets you resubmit in one click.", highlight: null, route: '/revq/denials' },
      { title: 'Batch Fix', text: "The batch AI Fix feature lets you fix multiple denials at once, saving hours of manual rework.", highlight: null, route: '/revq/denials' },
      { title: 'Revenue Forecast', text: "Finally, the revenue forecast uses machine learning on your historical data to project collections over the next 90 days.", highlight: null, route: '/revq/analytics' },
      { title: "You're All Set!", text: "That's the complete rev Q experience. AI handles the heavy lifting so your billing team can focus on exceptions, not routine work.", highlight: null, route: '/revq' },
    ],
  },
  lumina: {
    quick: [
      { title: 'Welcome to Lumina', text: "Welcome to Lumina, your AI clinical documentation assistant. Doctors have two ways to work: a focused Clinical Focus mode for quick SOAP notes, or the full Lumina workflow.", highlight: null, route: '/lumina' },
      { title: 'Clinical Focus', text: "Clinical Focus is the fastest path to a SOAP note. Enter it from the dashboard for a streamlined workspace: patient schedule, recording controls, and AI-generated notes.", highlight: '[data-testid="clinical-focus-card"]', route: '/lumina' },
      { title: 'Patient Charts', text: "Or work in the full Lumina workflow. Open any patient chart, start an ambient visit, and the AI generates a complete note you can copy, send as a CCDA, or message to another provider.", highlight: null, route: '/lumina/patients' },
      { title: "You're All Set!", text: "That's Lumina: AI-generated notes, one-click copy, two powerful workflows.", highlight: null, route: '/lumina' },
    ],
    standard: [
      { title: 'Welcome to Lumina', text: "Welcome to Lumina. Let's walk through the two clinical documentation workflows available to you.", highlight: null, route: '/lumina' },
      { title: 'Clinical Focus', text: "For fast, focused documentation, use Clinical Focus. It gives you a streamlined workspace: today's schedule on the left, recording controls in the center, and an AI notes panel on the right.", highlight: '[data-testid="clinical-focus-card"]', route: '/lumina' },
      { title: 'Ambient & Dictation', text: "In Clinical Focus, choose between ambient listening, which transcribes the patient conversation, or dictation for direct note capture. Both feed into AI SOAP generation.", highlight: null, route: '/lumina' },
      { title: 'Lens Upload', text: "Enrich any note with Lens uploads. Scan labs, imaging reports, or referrals and the AI extracts structured data to include in your documentation.", highlight: null, route: '/lumina' },
      { title: 'Full Workflow', text: "The full Lumina workflow works like a complete EHR. Open a patient chart, start an ambient visit from the header, and get a full SOAP note with coding suggestions and follow-up tasks.", highlight: null, route: '/lumina/patients' },
      { title: 'Multi-Output', text: "After generating a note, you have multiple output options: copy to EHR, download a CCDA document, or send via Direct Message. You can also enable auto Direct Message to draft one every time.", highlight: null, route: '/lumina' },
      { title: 'Workflow Complete', text: "That covers Lumina: from ambient listening to signed note to billing, all in under a minute.", highlight: null, route: '/lumina' },
    ],
    deep: [
      { title: 'Welcome', text: "Welcome to the Lumina deep dive. We'll cover both documentation workflows and all the interoperability features.", highlight: null, route: '/lumina' },
      { title: 'Two Workflows', text: "Lumina gives doctors two ways to work. Clinical Focus is the streamlined path for pure SOAP note generation. The full workflow is a complete clinical EHR experience.", highlight: null, route: '/lumina' },
      { title: 'Clinical Focus Entry', text: "Enter Clinical Focus from the dashboard. You'll see a focused workspace: patient schedule, session controls, and Lumina Notes panel.", highlight: '[data-testid="clinical-focus-card"]', route: '/lumina' },
      { title: 'Schedule & Selection', text: "The dark schedule panel on the left shows today's patients with status indicators. Click any patient to load their info and start the encounter.", highlight: null, route: '/lumina' },
      { title: 'Recording Modes', text: "Choose between ambient listening for recording the full patient encounter, or dictation for speaking your clinical observations directly. Both modes feed the AI transcript.", highlight: null, route: '/lumina' },
      { title: 'Lens Integration', text: "The Lens upload button lets you scan labs, imaging reports, referrals, or any clinical document. AI extracts the data and enriches your SOAP note automatically.", highlight: null, route: '/lumina' },
      { title: 'AI Note Generation', text: "When you stop recording, Lumina AI automatically generates a complete SOAP note with Subjective, Objective, Assessment, and Plan sections, including ICD ten codes.", highlight: null, route: '/lumina' },
      { title: 'Copy & Export', text: "The Copy Note button is always prominent. One click copies the full note for pasting into any EHR system. You can also copy individual sections.", highlight: null, route: '/lumina' },
      { title: 'CCDA Export', text: "For structured interoperability, download a CCDA document. This XML format is accepted by most EHR systems for direct import.", highlight: null, route: '/lumina' },
      { title: 'Direct Message', text: "Send notes via healthcare-standard Direct Messaging. Select a recipient, preview the message, and send with encryption end to end.", highlight: null, route: '/lumina' },
      { title: 'Auto Direct Message', text: "Enable auto Direct Message to automatically draft a secure message every time a SOAP note is generated. Review and send, or disable it anytime.", highlight: null, route: '/lumina' },
      { title: 'Full EHR Workflow', text: "In the full Lumina workflow, open a patient chart and start an ambient visit from the header. You get the same AI generation plus coding suggestions, follow-up tasks, and billing integration.", highlight: null, route: '/lumina/patients' },
      { title: "You're All Set!", text: "That's the complete Lumina experience: two powerful workflows, AI-powered notes, and seamless interoperability.", highlight: null, route: '/lumina' },
    ],
  },
  echo: {
    quick: [
      { title: 'Welcome to Echo', text: "Welcome to Echo, your AI-powered front office operations hub.", highlight: null, route: '/echo' },
      { title: 'Dashboard', text: "The dashboard gives you a real-time snapshot of today's clinic: patients checked in, average wait time, and appointment status.", highlight: null, route: '/echo' },
      { title: 'Patient Flow', text: "The patient flow board tracks every patient through stages from check-in to checkout, so nothing falls through the cracks.", highlight: null, route: '/echo/patients' },
      { title: "You're All Set!", text: "That's Echo: real-time clinic operations, smart scheduling, and AI-assisted patient flow.", highlight: null, route: '/echo' },
    ],
    standard: [
      { title: 'Welcome to Echo', text: "Welcome to Echo. Let's walk through how it manages your front office.", highlight: null, route: '/echo' },
      { title: "Today's Snapshot", text: "The dashboard's Today's Snapshot bar shows live metrics: patients checked in, waiting, in exam, and checked out.", highlight: null, route: '/echo' },
      { title: 'Workflow Guide', text: "The workflow guide walks front desk staff through 6 daily steps, from opening the schedule to end-of-day reconciliation.", highlight: '[data-testid="workflow-tracker"]', route: '/echo' },
      { title: 'Schedule', text: "The schedule view lets you see appointments in list or timeline format. Use Lens to upload a printed schedule and Echo digitizes it automatically.", highlight: null, route: '/echo/schedule' },
      { title: 'Patient Flow Board', text: "The patient flow board is a kanban-style view. Drag patients between stages as they move through the clinic. Room assignments and wait timers update in real time.", highlight: null, route: '/echo/patients' },
      { title: 'AI Phone Agents', text: "Echo also has AI phone agents that can handle appointment scheduling and patient inquiries automatically.", highlight: null, route: '/echo/agents' },
      { title: 'Workflow Complete', text: "That's the Echo workflow: one dashboard to run your entire front office.", highlight: null, route: '/echo' },
    ],
    deep: [
      { title: 'Welcome', text: "Welcome to the Echo deep dive. We'll cover the complete front office workflow from open to close.", highlight: null, route: '/echo' },
      { title: 'Unified Dashboard', text: "Echo is built around the idea that a front desk should never have to switch between systems. Everything is in one real-time dashboard.", highlight: null, route: '/echo' },
      { title: "Today's Metrics", text: "The Today's Snapshot bar gives you instant metrics. You can see exactly how many patients are at each stage right now.", highlight: null, route: '/echo' },
      { title: 'Workflow Guide', text: "The 6-step workflow guide is designed for front desk staff. Each step, from pulling the schedule to reconciling checkout, includes built-in Lens integration for scanning documents.", highlight: '[data-testid="workflow-tracker"]', route: '/echo' },
      { title: 'Schedule Views', text: "The schedule supports multiple views. List view for a quick scan, timeline view for time-block planning, and patient flow for real-time tracking.", highlight: null, route: '/echo/schedule' },
      { title: 'Patient Flow Board', text: "The patient flow board is the heart of Echo. It's a kanban board where each column represents a stage: waiting, checked in, vitals, in exam, checkout.", highlight: null, route: '/echo/patients' },
      { title: 'Room & Wait Tracking', text: "You can assign rooms, track wait times, and drag patients between stages. The system automatically logs timestamps for operational analytics.", highlight: null, route: '/echo/patients' },
      { title: 'Communication Hub', text: "The communication hub centralizes patient messages, calls, and follow-ups. The patient calls widget shows today's call volume and resolution rate.", highlight: null, route: '/echo/comms' },
      { title: 'AI Phone Agents', text: "AI phone agents are trained on your practice's scheduling rules. They can answer calls, book appointments, and handle common patient questions 24/7.", highlight: null, route: '/echo/agents' },
      { title: 'Analytics', text: "The operational dashboard gives you analytics on wait times, no-show rates, and staff efficiency so you can continuously improve.", highlight: null, route: '/echo/analytics' },
      { title: "You're All Set!", text: "That's the complete Echo experience: a single intelligent hub that runs your front office from first patient to last.", highlight: null, route: '/echo' },
    ],
  },
};

const GuidedDemoModal = ({ app = 'lumina', open, onOpenChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTier, setSelectedTier] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [highlightRect, setHighlightRect] = useState(null);
  const audioRef = useRef(null);
  const playingRef = useRef(false);

  const steps = useMemo(() => selectedTier ? (DEMO_SCRIPTS[app]?.[selectedTier] || []) : [], [selectedTier, app]);

  // Keep ref in sync with state
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  const clearHighlight = useCallback(() => {
    setHighlightRect(null);
  }, []);

  const updateHighlightRect = useCallback((selector) => {
    if (!selector) { setHighlightRect(null); return; }
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const rect = el.getBoundingClientRect();
      setHighlightRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16,
      });
    } else {
      setHighlightRect(null);
    }
  }, []);

  const playStep = useCallback(async (stepIdx) => {
    if (stepIdx >= steps.length) {
      setPlaying(false);
      setCompleted(true);
      clearHighlight();
      return;
    }

    setCurrentStep(stepIdx);
    const step = steps[stepIdx];

    // Navigate if needed
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
      // Wait for navigation to settle before highlighting
      setTimeout(() => updateHighlightRect(step.highlight), 600);
    } else {
      setTimeout(() => updateHighlightRect(step.highlight), 100);
    }

    if (muted) {
      setTimeout(() => {
        if (playingRef.current) playStep(stepIdx + 1);
      }, 3500);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/echo/tts/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: step.text, voice: 'nova', speed: 1.05 }),
      });
      const data = await res.json();
      setLoading(false);

      if (data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        audioRef.current = audio;
        audio.onended = () => {
          if (playingRef.current) playStep(stepIdx + 1);
        };
        audio.play().catch(() => {
          if (playingRef.current) playStep(stepIdx + 1);
        });
      } else {
        setTimeout(() => {
          if (playingRef.current) playStep(stepIdx + 1);
        }, 3500);
      }
    } catch {
      setLoading(false);
      setTimeout(() => {
        if (playingRef.current) playStep(stepIdx + 1);
      }, 3500);
    }
  }, [steps, muted, clearHighlight, updateHighlightRect, navigate, location.pathname]);

  const handleStart = (tier) => {
    setSelectedTier(tier);
    setCurrentStep(0);
    setPlaying(true);
    setCompleted(false);
    setHighlightRect(null);
  };

  useEffect(() => {
    if (playing && selectedTier && currentStep === 0 && steps.length > 0) {
      playStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTier]);

  // Resize handler
  useEffect(() => {
    if (!selectedTier || completed) return;
    const step = steps[currentStep];
    if (!step?.highlight) return;
    const handler = () => updateHighlightRect(step.highlight);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [selectedTier, completed, currentStep, steps, updateHighlightRect]);

  const handlePause = () => {
    setPlaying(false);
    audioRef.current?.pause();
  };

  const handleResume = () => {
    setPlaying(true);
    if (audioRef.current?.paused) {
      audioRef.current.play().catch(() => {});
    } else {
      playStep(currentStep);
    }
  };

  const handleSkip = () => {
    audioRef.current?.pause();
    if (playingRef.current) playStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      audioRef.current?.pause();
      playStep(currentStep - 1);
    }
  };

  const handleReplay = () => {
    audioRef.current?.pause();
    setCompleted(false);
    setCurrentStep(0);
    setPlaying(true);
    playStep(0);
  };

  const handleClose = () => {
    audioRef.current?.pause();
    clearHighlight();
    setPlaying(false);
    setSelectedTier(null);
    setCurrentStep(0);
    setCompleted(false);
    onOpenChange(false);
  };

  const appLabel = { revq: 'revQ', echo: 'Echo', lumina: 'Lumina' }[app] || app;

  // Tier selection screen
  if (!selectedTier) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="demo-tier-dialog">
          <div className="bg-black px-6 pt-6 pb-4 text-white">
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Play className="h-5 w-5" /> Start Guided Demo &mdash; {appLabel}
            </DialogTitle>
            <p className="text-sm text-slate-400 mt-1">Choose your pace. A voice assistant will walk you through {appLabel}.</p>
          </div>
          <div className="p-6 space-y-3">
            {TIERS.map(tier => {
              const Icon = tier.icon;
              return (
                <button
                  key={tier.id}
                  onClick={() => handleStart(tier.id)}
                  className={cn("w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all hover:shadow-md", tier.color)}
                  data-testid={`demo-tier-${tier.id}`}
                >
                  <div className="h-12 w-12 rounded-xl bg-white/80 flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-slate-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-slate-800">{tier.label}</p>
                      <Badge className="bg-white/60 text-slate-600 text-[9px] border-0">{tier.time}</Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">{tier.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const currentStepData = steps[currentStep];

  // Active demo with synced visual overlay
  return (
    <>
      {/* Dark overlay backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/40 pointer-events-none transition-opacity" />

      {/* Highlight cutout */}
      {highlightRect && (
        <div
          className="fixed z-[61] rounded-xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] pointer-events-none transition-all duration-500"
          style={{ top: highlightRect.top, left: highlightRect.left, width: highlightRect.width, height: highlightRect.height }}
          data-testid="tour-highlight"
        />
      )}

      {/* Visual Step Card (top area) */}
      {!completed && currentStepData && (
        <div className="fixed z-[62] top-24 left-1/2 -translate-x-1/2 w-[460px]" data-testid="tour-card">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            {/* Progress bar */}
            <div className="h-1 bg-slate-100">
              <div className="h-full bg-black transition-all duration-300" style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }} />
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {currentStep === steps.length - 1 ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Compass className="h-4 w-4 text-violet-500" />
                  )}
                  <span className="text-[10px] text-muted-foreground font-medium">{currentStep + 1} of {steps.length}</span>
                </div>
                <button onClick={handleClose} className="text-xs text-slate-400 hover:text-slate-700 font-medium" data-testid="tour-skip">
                  Exit Tour
                </button>
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-1">{currentStepData.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{currentStepData.text}</p>
              {/* Step navigation dots */}
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className={cn("flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors",
                    currentStep === 0 ? "border-slate-100 text-slate-300 cursor-not-allowed" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  )}
                  data-testid="tour-prev"
                >
                  <ArrowLeft className="h-3 w-3" /> Back
                </button>
                <div className="flex gap-1">
                  {steps.map((_, i) => (
                    <div key={i} className={cn("h-1.5 rounded-full transition-all",
                      i === currentStep ? "w-5 bg-black" : i < currentStep ? "w-1.5 bg-slate-400" : "w-1.5 bg-slate-200"
                    )} />
                  ))}
                </div>
                <button
                  onClick={handleSkip}
                  className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                  data-testid="tour-next"
                >
                  {currentStep === steps.length - 1 ? 'Finish' : 'Next'} <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio Player Bar (bottom) */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[62]" data-testid="demo-player">
        <div className="bg-black/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 px-5 py-3 flex items-center gap-4 min-w-[500px] max-w-[700px]">
          {completed ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white">Demo Complete</p>
                <p className="text-[10px] text-slate-400 truncate">You've completed the {TIERS.find(t => t.id === selectedTier)?.label}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={handleReplay} className="text-white hover:bg-white/10 gap-1 text-xs" data-testid="demo-replay-btn">
                <RotateCcw className="h-3 w-3" /> Replay
              </Button>
              <Button size="sm" onClick={handleClose} className="bg-white text-black hover:bg-slate-200 text-xs" data-testid="demo-done-btn">
                Done
              </Button>
            </>
          ) : (
            <>
              {/* Play/Pause */}
              <button
                onClick={playing ? handlePause : handleResume}
                className="h-9 w-9 rounded-full bg-white flex items-center justify-center shrink-0"
                data-testid="demo-play-pause"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 text-black animate-spin" />
                ) : playing ? (
                  <Pause className="h-4 w-4 text-black" />
                ) : (
                  <Play className="h-4 w-4 text-black ml-0.5" />
                )}
              </button>

              {/* Text / Progress */}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white font-medium truncate" data-testid="demo-caption">
                  {steps[currentStep]?.text || ''}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }} />
                  </div>
                  <span className="text-[9px] text-slate-500">{currentStep + 1}/{steps.length}</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setMuted(!muted)} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10" data-testid="demo-mute">
                  {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                </button>
                <button onClick={handleSkip} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10" data-testid="demo-skip">
                  <SkipForward className="h-3.5 w-3.5" />
                </button>
                <button onClick={handleClose} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10" data-testid="demo-close">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default GuidedDemoModal;
